import { useMemo } from "react";
import { useSelector } from "react-redux";
import { AttributeType } from "@evergis/api";

import { getClassificationParams } from "../../ducks/location/selectors";
import { getAppConfig } from "../../ducks/appConfig";
import { useCurrentLayerAttributeNames } from "../layer/useCurrentLayerAttributes";
import { useCurrentLayerAttribute } from "../layer/useCurrentLayerAttribute";
import { clampClassCount, isValidAttribute, isValidParameter } from "./checkQuery";
import { useSegmentStyle } from "../../modules/Style/hooks/useSegmentStyle";
import { isClusterFillColor as checkIsClusterFillColor } from "../../interfaces/style";
import { ClassifyType } from "../../interfaces/classification";
import { ClassificationQuery } from "../../ducks/location/location";

type ClassificationQueryParams = Omit<
  Required<OmitAndRename<ClassificationQuery, "field", "attributeName">>,
  "dialog"
> & {
  attributeType: AttributeType;
};

export const useClassificationQueryParams = (): ClassificationQueryParams => {
  const appConfig = useSelector(getAppConfig);
  const params = useSelector(getClassificationParams);
  const style = useSegmentStyle();
  const attributes = useCurrentLayerAttributeNames();
  const attribute = useCurrentLayerAttribute(params.field, params.parameter);

  return useMemo(() => {
    const isClusterFillColor = checkIsClusterFillColor(style.symbol, params.parameter);
    const isValid = params && (isValidAttribute(params.field, attributes) || isClusterFillColor);
    const type = (params && params.type) || ("equalInterval" as ClassifyType);

    return {
      attributeName: isValid ? params.field : undefined,
      attributeType: isClusterFillColor ? AttributeType.Int32 : isValid ? attribute?.type : undefined,
      parameter: params && isValidParameter(params.parameter) ? params.parameter : undefined,
      valueIndex: params.valueIndex || 0,
      count: params && !Number.isNaN(params.count) ? clampClassCount(+params.count, type, appConfig) : undefined,
      type,
      minQuantity: params.minQuantity,
      maxQuantity: params.maxQuantity,
    };
  }, [style.symbol, params, attributes, attribute?.type, appConfig]);
};
